import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  FileText,
  Edit,
  Trash2,
  Plus,
  Home,
  Loader2,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import PropertyInsurance from "../components/PropertyInsurance";

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isManager } = useAuth();
  const [property, setProperty] = useState(null);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchProperty = async () => {
    try {
      const [propRes, ownersRes] = await Promise.all([
        axios.get(`${API}/properties/${id}`, { headers }),
        axios.get(`${API}/owners`, { headers })
      ]);
      setProperty(propRes.data);
      setOwners(ownersRes.data);
      setFormData({
        name: propRes.data.name,
        address: propRes.data.address,
        owner_id: propRes.data.owner_id,
        property_type: propRes.data.property_type,
        units: propRes.data.units,
        has_basement: propRes.data.has_basement,
        description: propRes.data.description || ""
      });
    } catch (error) {
      toast.error("Failed to load property");
      navigate("/properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperty();
  }, [id, token]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/properties/${id}`, formData, { headers });
      toast.success("Property updated successfully");
      setEditDialogOpen(false);
      fetchProperty();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update property");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/properties/${id}`, { headers });
      toast.success("Property deleted successfully");
      navigate("/properties");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete property");
    }
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="skeleton h-64 rounded-xl" />
      </div>
    );
  }

  if (!property) return null;

  return (
    <div className="animate-fade-in" data-testid="property-detail-page">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate("/properties")} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Properties
        </Button>
        
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
              <Home className="w-8 h-8 text-green-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {property.name}
              </h1>
              <div className="flex items-center gap-1 text-slate-500 mt-1">
                <MapPin className="w-4 h-4" />
                {property.address}
              </div>
            </div>
          </div>
          
          {isManager && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="edit-property-button">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-property-button">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Property Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Property Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Owner</p>
              <p className="font-medium text-slate-900">{property.owner?.name}</p>
              <Badge className={property.owner?.owner_type === "llc" ? "bg-blue-100 text-blue-700 mt-1" : "bg-green-100 text-green-700 mt-1"}>
                {property.owner?.owner_type === "llc" ? "LLC" : "Personal"}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Property Type</p>
              <p className="font-medium text-slate-900 capitalize">{property.property_type}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Units</p>
              <p className="font-medium text-slate-900">{property.units}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Basement</p>
              <p className="font-medium text-slate-900">{property.has_basement ? "Yes" : "No"}</p>
            </div>
            {property.description && (
              <div className="col-span-2">
                <p className="text-sm text-slate-500 mb-1">Description</p>
                <p className="text-slate-700">{property.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Stats</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">Tenants</span>
              </div>
              <span className="font-semibold">{property.tenants?.length || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">Active Leases</span>
              </div>
              <span className="font-semibold">
                {property.leases?.filter(l => l.status === "active").length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">Insurance</span>
              </div>
              {property.insurance?.expiry_date ? (
                <Badge className={
                  new Date(property.insurance.expiry_date) < new Date() 
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                }>
                  {new Date(property.insurance.expiry_date) < new Date() ? "Expired" : "Active"}
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700">Missing</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Tenants, Leases, Insurance */}
      <Tabs defaultValue="tenants" className="space-y-6">
        <TabsList>
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Tenants
          </TabsTrigger>
          <TabsTrigger value="leases" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Leases
          </TabsTrigger>
          <TabsTrigger value="insurance" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Insurance
            {!property.insurance && (
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-500" />
                Tenants
              </CardTitle>
              {isManager && (
                <Link to="/tenants">
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Tenant
                  </Button>
                </Link>
              )}
            </CardHeader>
            <CardContent>
              {property.tenants?.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No tenants assigned to this property</p>
              ) : (
                <div className="space-y-3">
                  {property.tenants?.map((tenant) => (
                    <Link
                      key={tenant.id}
                      to={`/tenants`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{tenant.name}</p>
                        {tenant.unit_number && (
                          <p className="text-sm text-slate-500">Unit {tenant.unit_number}</p>
                        )}
                      </div>
                      <div className="text-right text-sm text-slate-500">
                        {tenant.email && <p>{tenant.email}</p>}
                        {tenant.phone && <p>{tenant.phone}</p>}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leases Tab */}
        <TabsContent value="leases">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-500" />
                Leases
              </CardTitle>
              {isManager && (
                <Link to="/leases">
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Create Lease
                  </Button>
                </Link>
              )}
            </CardHeader>
            <CardContent>
              {property.leases?.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No leases for this property</p>
              ) : (
                <div className="space-y-3">
                  {property.leases?.map((lease) => (
                    <div
                      key={lease.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{lease.tenant_name || "Tenant"}</p>
                        <p className="text-sm text-slate-500">
                          {lease.start_date} to {lease.end_date}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-700">
                          ${(lease.monthly_rent || lease.rent_amount || 0).toLocaleString()}/mo
                        </p>
                        <Badge className={
                          lease.status === "active" ? "bg-green-100 text-green-700" :
                          lease.status === "month_to_month" ? "bg-blue-100 text-blue-700" :
                          "bg-slate-100 text-slate-700"
                        }>
                          {(lease.status || "active").replace("_", "-")}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance Tab */}
        <TabsContent value="insurance">
          <PropertyInsurance property={property} onUpdate={fetchData} />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Property</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label>Property Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Owner</Label>
              <Select
                value={formData.owner_id}
                onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {owners.map((owner) => (
                    <SelectItem key={owner.id} value={owner.id}>
                      {owner.name} ({owner.owner_type === "llc" ? "LLC" : "Personal"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Property Type</Label>
                <Select
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Units</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.units}
                  onChange={(e) => setFormData({ ...formData, units: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit_has_basement"
                checked={formData.has_basement}
                onChange={(e) => setFormData({ ...formData, has_basement: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="edit_has_basement" className="cursor-pointer">Has Basement</Label>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{property.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PropertyDetail;
